import * as THREE from 'three';
import { BLOCK_HITS_TO_BREAK, CREEP_SPEED, GRID_H, GRID_W } from '../sim/constants.ts';
import { idx } from '../sim/room.ts';
import { ChimeraKind, ItemKind, Phase, Tile, type SimEvent, type SimState, type TileKind } from '../sim/types.ts';
import { PlaceholderAxie, type AxieCharacter } from './AxieCharacter.ts';
import { createSlimeBuddyModel } from './createSlimeBuddyModel.ts';
import { PortalEffect } from './PortalEffect.ts';
import type { EmeraldPreview } from './EmeraldPreview.ts';
import type { MoonseedEmblem } from './MoonseedEmblem.ts';
import type { SlimeBuddyPreview } from './SlimeBuddyPreview.ts';

/**
 * The Three.js view.
 *
 * Owns its renderer, scene and every GPU resource it allocates, and lives
 * entirely outside React. React Strict Mode double-mounts in development, so a
 * renderer owned by a component leaks a WebGL context and a full scene graph on
 * every mount — which is brief risk R-07, and it fails at exactly the moment a
 * judge is playing. `dispose()` is the contract that prevents it.
 *
 * The camera is orthographic and framed to exactly the room: this is a
 * single-screen 2D game drawn with a 3D renderer, which is also what makes the
 * 2D-native Axie Mixer a natural fit (brief §12).
 *
 * Simulation y increases downward; the view negates it here and nowhere else.
 */

const PALETTE = {
  background: 0x131033,
  stone: 0x2a2456,
  seed: 0x4a3f8f,
  vine: 0x3c6b52,
  gold: 0xffc65c, // reserved: interactive and collectable only
  player: 0x8be9fd,
  fireball: 0xff6a3d, // the Tail skill's shot (brief §7's Bramble Horn unlock) - reserved for it alone
} as const;

/** How long a fired shot's beam stays visible before fully fading, in
 *  seconds. The shot itself resolves instantly in one sim tick (brief §5's
 *  determinism rule leaves no room for a travelling projectile) - this is
 *  purely a render-side flash so the press has an on-screen effect at all,
 *  never a hitbox in its own right. */
const BEAM_LIFETIME = 0.15;

const MARGIN = 0.5;

/**
 * How far a fully-damaged block would be lightened toward white.
 *
 * Applied in proportion to damage, so a block one hit from breaking sits at
 * half of this. A struck block is invisible state that changes what the next
 * jump does, and the player has no other way to read it; lightening is the
 * cheapest honest tell, and because it moves luminance rather than hue it
 * survives the colourblind-safe rule (brief §11).
 *
 * Kept low deliberately. Three.js lerps in linear space, so this is a bigger
 * perceptual step than it looks: 0.2 already lifts a Seedblock's luminance
 * ~2.3x at one hit. Any more and a struck block stops reading as the same
 * material, which would break the rule in §12 that block types must stay
 * distinguishable at a glance.
 */
const HIT_TINT_MAX = 0.2;
const HIT_TINT = new THREE.Color(0xffffff);

/**
 * Four-step toon gradient map for the tile material.
 *
 * `MeshToonMaterial` looks up N·L against this texture instead of shading it
 * smoothly, which is what turns the existing rim light into a visible band on
 * each block's lit edge rather than a soft gradient. Generated in-code so no
 * texture file is ever committed (brief §12, "zero external asset kits") -
 * the same reasoning that keeps SFX synthesized rather than sampled (§13).
 */
function makeToonGradientMap(): THREE.DataTexture {
  const bands = new Uint8Array([70, 130, 190, 255]);
  const tex = new THREE.DataTexture(bands, bands.length, 1, THREE.RedFormat);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Deterministic PRNG (mulberry32) for procedural textures.
 *
 * Not a simulation concern - Pillar 2's no-RNG rule is about the sim, not
 * pixels - but a texture that reshuffled itself on every reload would be a
 * distracting bug report waiting to happen, so it is seeded rather than
 * sampled from `Math.random()`.
 */
function mulberry32(seed: number): () => number {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeDetailTexture(draw: (ctx: CanvasRenderingContext2D, size: number) => void): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  draw(ctx, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Mottled, cracked-stone detail texture for `Tile.Stone`.
 *
 * Grayscale on a white base, multiplied into the instance colour the same
 * way the baked bevel is (`map` and vertex `color` both feed the standard
 * colour chunk), so it darkens Stone's existing palette colour rather than
 * introducing a new one. The permanent, uncastable tile earns a texture with
 * no directionality - unlike the brick coursing below, cracks read as inert
 * damage rather than a structure the player could open.
 */
function makeStoneTexture(): THREE.CanvasTexture {
  return makeDetailTexture((ctx, size) => {
    const rand = mulberry32(1);
    for (let i = 0; i < 46; i++) {
      const x = rand() * size;
      const y = rand() * size;
      const r = 1 + rand() * 2.2;
      const shade = 145 + Math.floor(rand() * 55);
      ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = 'rgba(110,110,110,0.85)';
    ctx.lineWidth = 1.4;
    for (let c = 0; c < 3; c++) {
      let x = rand() * size;
      let y = rand() * size;
      ctx.beginPath();
      ctx.moveTo(x, y);
      const segments = 3 + Math.floor(rand() * 3);
      for (let s = 0; s < segments; s++) {
        x += (rand() - 0.5) * size * 0.5;
        y += (rand() - 0.5) * size * 0.5;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  });
}

/**
 * Brick-and-mortar detail texture, shared by `Tile.Seed` and `Tile.Vine` -
 * both castable, both the "breakable" family. The coursing gives them a
 * built, structural read that Stone's cracks deliberately don't have: this
 * is the block the player is meant to see as something they could open.
 */
function makeBrickTexture(): THREE.CanvasTexture {
  return makeDetailTexture((ctx, size) => {
    const rows = 4;
    const rowH = size / rows;
    ctx.strokeStyle = 'rgba(100,100,100,0.9)';
    ctx.lineWidth = 2;
    for (let r = 0; r <= rows; r++) {
      const y = r * rowH;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(size, y);
      ctx.stroke();
    }
    for (let r = 0; r < rows; r++) {
      const y0 = r * rowH;
      const y1 = y0 + rowH;
      const offset = r % 2 === 0 ? 0 : size / 4;
      for (let x = -size / 4; x <= size + size / 4; x += size / 2) {
        const xx = x + offset;
        ctx.beginPath();
        ctx.moveTo(xx, y0);
        ctx.lineTo(xx, y1);
        ctx.stroke();
      }
    }
  });
}

/**
 * A pixel-crisp impact fissure for a block with exactly one head-butt.
 *
 * The dark, forked core and warm chipped edge are drawn at a deliberately low
 * resolution, matching the reference's legibility at tile scale. It is an
 * alpha-only overlay rather than a replacement tile texture, so Seed and Vine
 * retain their own material identity below the damage mark.
 */
function makeHitCrackTexture(): THREE.CanvasTexture {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  ctx.imageSmoothingEnabled = false;

  const stroke = (points: readonly [number, number][], colour: string, width: number): void => {
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.lineJoin = 'miter';
    ctx.lineCap = 'square';
    ctx.beginPath();
    points.forEach(([x, y], index) => {
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  };

  const spine: Array<[number, number]> = [[18, 3], [16, 8], [19, 12], [14, 17], [16, 22], [11, 29]];
  const upperFork: Array<[number, number]> = [[16, 8], [11, 10], [8, 7]];
  const lowerFork: Array<[number, number]> = [[14, 17], [9, 19], [7, 24]];
  // Chipped highlight first, then a thinner dark fissure above it.
  for (const path of [spine, upperFork, lowerFork]) stroke(path, 'rgba(255, 214, 138, 0.62)', 3);
  for (const path of [spine, upperFork, lowerFork]) stroke(path, 'rgba(29, 17, 46, 0.96)', 1.4);

  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * A door's face: its label (the room number it leads to) baked into a
 * canvas texture, tinted gold when open and dimmed near-Stone when locked -
 * the same "gold means interactive" and "Stone means permanent/inert" reads
 * the rest of the palette already uses, applied to a new object instead of
 * introducing a third visual language.
 */
function makeDoorTexture(label: string, locked: boolean): THREE.CanvasTexture {
  const w = 64;
  const h = 96;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  const fill = locked ? '#2a2456' : '#6b4a12';
  const frame = locked ? '#43406f' : '#ffc65c';
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = frame;
  ctx.lineWidth = 5;
  ctx.strokeRect(4, 4, w - 8, h - 8);
  // Arched top, the one universally "door-shaped" cue.
  ctx.beginPath();
  ctx.moveTo(4, h * 0.4);
  ctx.quadraticCurveTo(4, 4, w / 2, 4);
  ctx.quadraticCurveTo(w - 4, 4, w - 4, h * 0.4);
  ctx.stroke();

  ctx.fillStyle = locked ? '#8b87b8' : '#fff4d6';
  ctx.font = 'bold 34px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, w / 2, h * 0.42);

  if (locked) {
    // A small padlock drawn as shapes, not a font glyph - readable the same
    // way on every platform regardless of emoji font support.
    const cx = w / 2;
    const cy = h * 0.72;
    ctx.strokeStyle = '#c9c6e8';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(cx, cy - 6, 8, Math.PI, 0, false);
    ctx.stroke();
    ctx.fillStyle = '#c9c6e8';
    ctx.fillRect(cx - 11, cy - 6, 22, 16);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Uniform scale applied to every Chimera's Slime Buddy model. The model's
 * own body geometry has a natural horizontal radius of ~1.02 units; the
 * Chimera silhouette this replaces was a 0.8-unit box (half-extent 0.4), so
 * this keeps the same on-screen footprint rather than introducing a new one.
 */
const CHIMERA_MODEL_SCALE = 0.4;

/**
 * Chimera animation tuning.
 *
 * Two effects, chosen to read as "alive" without touching `sim.ts`: a
 * continuous idle bounce/jiggle (every Chimera, all the time, phase-offset
 * per instance so a room full of them doesn't pulse in lockstep), and a
 * movement-reactive squash/stretch driven purely by how far each Chimera's
 * own position actually moved since the last rendered frame - which
 * distinguishes a Creep's continuous slide from a Drifter/Warden's discrete
 * one-tile step without needing to know which kind it is.
 */
const CHIMERA_IDLE_BOB = 0.03;
const CHIMERA_IDLE_SQUASH = 0.035;
const CHIMERA_IDLE_RATE = 0.05; // radians per sim tick, same scale as the portal/item spin
/** A per-frame position delta past this (tiles) can only be a discrete
 *  Drifter/Warden step - continuous Creep motion never moves this far in one
 *  rendered frame - so it is read as a "hop" rather than a slide. */
const CHIMERA_HOP_DISTANCE = 0.25;
const CHIMERA_HOP_DURATION = 0.18; // seconds, pop-and-settle
const CHIMERA_HOP_SQUASH = 0.22;
const CHIMERA_SLIDE_SQUASH = 0.16;
/** Same quarter-turn convention as `GlbAxie.setFacing`, so the slime and
 *  Kotaro read as facing the same way for the same reason. */
const CHIMERA_FACING_YAW = Math.PI * 0.25;

interface ChimeraVisual {
  model: THREE.Group;
  /** Deterministic per-instance offset for the idle bounce, seeded from the
   *  Chimera's own id rather than randomised, so a replay/rerender is
   *  visually identical every time. */
  phase: number;
  prevX: number;
  prevY: number;
  /** Counts down from `CHIMERA_HOP_DURATION` after a detected discrete step. */
  hopT: number;
  /** Last observed horizontal travel direction. Persists across frames where
   *  a Chimera isn't moving (idle, mid-hop-decay, boxed against a wall) so
   *  facing never flickers back to a default - only real movement changes it. */
  facing: 1 | -1;
}

/** Highlight/shadow brightness multipliers for the baked tile bevel. */
const BEVEL_HIGHLIGHT = 1.4;
const BEVEL_SHADOW = 0.55;
/** Border thickness in tile-local units (a tile spans -0.5..0.5). */
const BEVEL_BORDER = 0.12;

/**
 * A front-facing 9-sliced tile quad with a baked-in highlight/shadow bevel,
 * in place of a plain flat square.
 *
 * The camera is a fixed orthographic front view that never rotates (brief
 * §5), so a tile's side and back faces are never seen - this is a flat plane,
 * not a box, which is both cheaper and the reason a *lit* bevel (a real
 * chamfered edge catching the rim light) read as barely-there: every visible
 * point shares one face normal no matter how the geometry is shaped. This
 * bakes the highlight (top/left) and shadow (bottom/right) directly into the
 * geometry's per-vertex `color` attribute instead, the way a hand-drawn sprite
 * block is shaded - a static tell that survives at any light angle.
 *
 * `MeshToonMaterial`'s `vertexColors` flag multiplies this baked pattern by
 * each instance's runtime colour (tile kind, then the hit-damage tint), so
 * the bevel and the existing colour system compose without special-casing
 * either - no palette colour changes.
 */
function makeBevelledTileGeometry(): THREE.BufferGeometry {
  const xs = [-0.5, -0.5 + BEVEL_BORDER, 0.5 - BEVEL_BORDER, 0.5];
  const ys = [-0.5, -0.5 + BEVEL_BORDER, 0.5 - BEVEL_BORDER, 0.5];

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  let v = 0;
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      // Top row and left column are lit; bottom row and right column are in
      // shadow. Top/bottom claim the corners, matching the classic raised-
      // block look where the top edge stays bright corner to corner.
      const tint = row === 2 ? BEVEL_HIGHLIGHT : row === 0 ? BEVEL_SHADOW : col === 0 ? BEVEL_HIGHLIGHT : col === 2 ? BEVEL_SHADOW : 1;
      const x0 = xs[col]!;
      const x1 = xs[col + 1]!;
      const y0 = ys[row]!;
      const y1 = ys[row + 1]!;
      const corners: Array<[number, number]> = [
        [x0, y0],
        [x1, y0],
        [x1, y1],
        [x0, y1],
      ];
      for (const [x, y] of corners) {
        positions.push(x, y, 0);
        normals.push(0, 0, 1);
        colors.push(tint, tint, tint);
      }
      uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
      indices.push(v, v + 1, v + 2, v, v + 2, v + 3);
      v += 4;
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geo.setIndex(indices);
  return geo;
}

export class Renderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  private readonly disposables: Array<{ dispose(): void }> = [];

  /** One instanced mesh per breakable/permanent tile kind, so Stone's cracked
   *  texture and Seed/Vine's brick texture can differ (Empty is never drawn). */
  private readonly tileMeshes: ReadonlyMap<TileKind, THREE.InstancedMesh>;
  /** One reusable, front-facing crack decal per single-hit castable block.
   *  Its matrices are rebuilt beside the tile matrices, never allocated one
   *  mesh per hit. */
  private readonly hitCrackMesh: THREE.InstancedMesh;
  private readonly itemMesh: THREE.InstancedMesh;
  /** One procedural Slime Buddy model (plus its animation state) per living
   *  Chimera, keyed by its stable `id` so both persist across frames instead
   *  of being rebuilt each tick. Chimera behaviour (movement, AI, kill rules)
   *  is entirely a sim.ts concern and is untouched here - this only changes
   *  what a Chimera looks like. */
  private readonly chimeraModels = new Map<number, ChimeraVisual>();
  /** One quad per door, keyed by its stable `id`. A door's label and locked
   *  state never change mid-visit (the hub is rebuilt fresh on every return
   *  to it - see `buildGroveHub`), so each is created once and left alone
   *  beyond a small idle bob. */
  private readonly doorMeshes = new Map<number, THREE.Mesh>();
  private readonly doorGeometry: THREE.PlaneGeometry;
  /** One quad per shot, unit-width so it can be scaled to the shot's real
   *  travelled distance. Short-lived: created on 'ranged:fired', faded and
   *  disposed within `BEAM_LIFETIME`, never reused across shots - a fired
   *  shot is a rare, momentary event, not a per-frame cost worth pooling. */
  private readonly beamGeometry = new THREE.PlaneGeometry(1, 0.18);
  private readonly activeBeams: Array<{ mesh: THREE.Mesh; life: number }> = [];
  private character: AxieCharacter;
  /** The portal is a code-only effect whose mutable motion stays render-side;
   *  the simulation retains sole ownership of its position, hidden state and
   *  win condition. */
  private readonly portal: PortalEffect;
  /** Dev-only, opt-in (`showSlimeBuddyPreview()`). Absent by default, so it
   *  costs nothing on the normal gameplay path. */
  private slimeBuddy: SlimeBuddyPreview | null = null;
  /** Dev-only, opt-in (`showEmeraldPreview()`). Absent by default, so it
   *  costs nothing on the normal gameplay path. */
  private emerald: EmeraldPreview | null = null;
  /** The Moonseed's real visual, loaded asynchronously (see the constructor)
   *  so it never sits between a judge and Room 1 - the same reasoning as the
   *  Axie GLB below. `null` until it resolves; the generic item gem covers
   *  for it exactly like `PlaceholderAxie` does for the real character. */
  private moonseedEmblem: MoonseedEmblem | null = null;

  /** Reused across frames rather than allocated per `render()` call. */
  private readonly tileCounts = new Map<TileKind, number>();
  private readonly dummy = new THREE.Object3D();
  private readonly hitCrackDummy = new THREE.Object3D();
  private readonly color = new THREE.Color();
  private readonly canvas: HTMLCanvasElement;
  private resizeObserver: ResizeObserver | null = null;
  private disposed = false;

  /**
   * Takes a container and creates its own canvas inside it, rather than being
   * handed one.
   *
   * This matters more than it looks. `dispose()` calls `forceContextLoss()` to
   * release the WebGL context promptly, and a canvas that has had its context
   * force-lost can never obtain another one. React reuses the same DOM node
   * across a Strict Mode remount, so a renderer handed a canvas from a ref
   * destroys that canvas for its own second mount. Owning the canvas means each
   * renderer instance gets a clean one and disposal is genuinely idempotent.
   */
  constructor(container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.setAttribute('aria-label', 'Moonseed Portals game view');
    this.canvas.style.display = 'block';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    container.appendChild(this.canvas);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setClearColor(PALETTE.background, 1);
    // Capped pixel ratio: uncapped DPR on a phone is the cheapest way to miss
    // the 60fps acceptance criterion (brief §15).
    this.renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio || 1, 2));

    this.camera = new THREE.OrthographicCamera(0, GRID_W, 0, -GRID_H, 0.1, 100);
    this.camera.position.set(0, 0, 10);

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const rim = new THREE.DirectionalLight(0x8be9fd, 1.1);
    rim.position.set(-3, 4, 6);
    this.scene.add(rim);

    const box = makeBevelledTileGeometry();
    const tileGradientMap = makeToonGradientMap();
    const stoneTexture = makeStoneTexture();
    const brickTexture = makeBrickTexture();
    const hitCrackTexture = makeHitCrackTexture();
    const tileMeshes = new Map<TileKind, THREE.InstancedMesh>();
    const tileMaterials: THREE.MeshToonMaterial[] = [];
    for (const [kind, map] of [
      [Tile.Stone, stoneTexture],
      [Tile.Seed, brickTexture],
      [Tile.Vine, brickTexture],
    ] as const) {
      const mat = new THREE.MeshToonMaterial({ gradientMap: tileGradientMap, vertexColors: true, map });
      tileMaterials.push(mat);
      const mesh = new THREE.InstancedMesh(box, mat, GRID_W * GRID_H);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // `InstancedMesh` lazily computes and caches its own bounding sphere from
      // whichever instances exist the first time it is frustum-tested - and
      // never recomputes it as instances are added or removed later. A tile
      // kind with zero instances at mount (Room 1 has no authored Vine, and no
      // Seed exists until the player casts one) caches an empty sphere and
      // stays invisible forever, even once real instances exist: a player-cast
      // block that silently never appears. The room is a fixed single screen
      // with no scrolling (brief §5) - nothing here is ever off-camera - so
      // culling has no upside to weigh against that failure mode.
      mesh.frustumCulled = false;
      this.scene.add(mesh);
      tileMeshes.set(kind, mesh);
    }
    this.tileMeshes = tileMeshes;

    const hitCrackGeometry = new THREE.PlaneGeometry(0.82, 0.82);
    const hitCrackMaterial = new THREE.MeshBasicMaterial({
      map: hitCrackTexture,
      transparent: true,
      alphaTest: 0.05,
      depthWrite: false,
    });
    this.hitCrackMesh = new THREE.InstancedMesh(hitCrackGeometry, hitCrackMaterial, GRID_W * GRID_H);
    this.hitCrackMesh.frustumCulled = false;
    this.hitCrackMesh.renderOrder = 1;
    this.scene.add(this.hitCrackMesh);

    const gem = new THREE.OctahedronGeometry(0.28);
    const gemMat = new THREE.MeshLambertMaterial({ color: PALETTE.gold, emissive: 0x4a2f00 });
    this.itemMesh = new THREE.InstancedMesh(gem, gemMat, 64);
    // Same stale-bounding-sphere hazard as the tile meshes above.
    this.itemMesh.frustumCulled = false;
    this.scene.add(this.itemMesh);

    // Starts on the placeholder so the renderer is usable the moment it is
    // constructed. Swap in the real Axie later with setCharacter(), which keeps
    // the toolkit and its 8.6 MB manifest off the path to Room 1 (risk R-03).
    this.character = new PlaceholderAxie(PALETTE.player);
    this.scene.add(this.character.object3D);

    this.portal = new PortalEffect();
    this.scene.add(this.portal.object3D);

    // Sized to the door texture's 64:96 aspect ratio, at roughly one tile
    // tall - big enough to read as a door, not so big it swallows the
    // landing a player just built to reach it.
    this.doorGeometry = new THREE.PlaneGeometry(0.7, 1.05);

    this.disposables.push(
      box,
      tileGradientMap,
      stoneTexture,
      brickTexture,
      hitCrackTexture,
      ...tileMaterials,
      hitCrackGeometry,
      hitCrackMaterial,
      this.hitCrackMesh,
      gem,
      gemMat,
      this.portal,
      this.doorGeometry,
      this.beamGeometry,
      ...tileMeshes.values(),
      this.itemMesh,
    );

    // Not awaited, same reasoning as the Axie GLB (`useGame.ts`): the
    // Moonseed still reads correctly as the generic gold item gem in the
    // meantime (see the `render()` skip condition below), so this never
    // sits between a judge and Room 1 (brief risk R-03) despite being
    // 59,232 triangles of imported model rather than a few dozen.
    void import('./MoonseedEmblem.ts')
      .then(({ MoonseedEmblem }) => {
        if (this.disposed) return;
        this.moonseedEmblem = new MoonseedEmblem(this.scene, this.renderer);
      })
      // Strict Mode's throwaway first mount can dispose this renderer before
      // the dynamic import resolves - expected there, not a real failure.
      .catch(() => {});

    this.observeResize();
  }

  private observeResize(): void {
    const parent = this.canvas.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') {
      this.resize(this.canvas.clientWidth || 800, this.canvas.clientHeight || 600);
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      const r = parent.getBoundingClientRect();
      this.resize(r.width, r.height);
    });
    this.resizeObserver.observe(parent);
    const r = parent.getBoundingClientRect();
    this.resize(r.width || 800, r.height || 600);
  }

  /**
   * The underlying WebGLRenderer, which the Axie Mixer needs in order to read
   * GPU extension support when choosing texture formats. Exposed for that one
   * purpose - nothing else should render through it.
   */
  get rendererHandle(): THREE.WebGLRenderer {
    return this.renderer;
  }

  /** True once dispose() has run. Guards the loop against a late frame. */
  get isDisposed(): boolean {
    return this.disposed;
  }

  /**
   * Replace the character model, disposing the previous one.
   *
   * The renderer owns the character's lifetime once handed over, so a caller
   * must not dispose what it passes in.
   */
  setCharacter(next: AxieCharacter): void {
    if (this.disposed || next === this.character) {
      if (this.disposed) next.dispose();
      return;
    }
    this.scene.remove(this.character.object3D);
    this.character.dispose();
    this.character = next;
    this.scene.add(next.object3D);
  }

  /** Visual-only response to a successful block cast. */
  playCharacterAttack(): void {
    this.character.playAttack();
  }

  /**
   * Mounts the procedural Slime Buddy dev preview into this renderer's
   * existing scene/camera/canvas (see `SlimeBuddyPreview`) and returns the
   * controller so a caller can drive `setBounce`/`setExplode`. Idempotent:
   * calling it again returns the already-mounted instance rather than
   * creating a second one.
   *
   * Dynamically imported, same reasoning as `AxieCharacter`'s GLTFLoader
   * import: a dev-only feature nobody on the judged path ever triggers
   * should not add one byte to the bundle everybody downloads to reach
   * Room 1 (brief risk R-03).
   */
  async showSlimeBuddyPreview(): Promise<SlimeBuddyPreview> {
    if (this.slimeBuddy) return this.slimeBuddy;
    const { SlimeBuddyPreview } = await import('./SlimeBuddyPreview.ts');
    if (this.disposed) throw new Error('Renderer disposed before Slime Buddy preview loaded');
    this.slimeBuddy ??= new SlimeBuddyPreview(this.scene, this.camera, this.canvas);
    return this.slimeBuddy;
  }

  /**
   * Mounts the reused Emerald Leaf Emblem dev preview into this renderer's
   * existing scene (see `EmeraldPreview`) and returns the controller so a
   * caller can drive `setSpin`/`setExplode`. Idempotent, same contract as
   * `showSlimeBuddyPreview` above.
   *
   * Dynamically imported for the same reason: a dev-only feature nobody on
   * the judged path ever triggers should not add one byte to the bundle
   * everybody downloads to reach Room 1 (brief risk R-03) - doubly true
   * here, since this model also pulls in `RectAreaLightUniformsLib`.
   */
  async showEmeraldPreview(): Promise<EmeraldPreview> {
    if (this.emerald) return this.emerald;
    const { EmeraldPreview } = await import('./EmeraldPreview.ts');
    if (this.disposed) throw new Error('Renderer disposed before Emerald preview loaded');
    this.emerald ??= new EmeraldPreview(this.scene, this.renderer);
    return this.emerald;
  }

  /** Letterbox, never crop: the whole room must be visible at any aspect ratio
   *  down to 375px wide (brief §5, §15). */
  resize(width: number, height: number): void {
    if (this.disposed || width <= 0 || height <= 0) return;
    this.renderer.setSize(width, height, false);

    const roomW = GRID_W + MARGIN * 2;
    const roomH = GRID_H + MARGIN * 2;
    const scale = Math.min(width / roomW, height / roomH);
    const halfW = width / (2 * scale);
    const halfH = height / (2 * scale);
    const cx = GRID_W / 2;
    const cy = -GRID_H / 2;

    this.camera.left = cx - halfW;
    this.camera.right = cx + halfW;
    this.camera.top = cy + halfH;
    this.camera.bottom = cy - halfH;
    this.camera.updateProjectionMatrix();
  }

  render(s: SimState, deltaSeconds = 0, events: readonly SimEvent[] = []): void {
    if (this.disposed) return;

    if (events.some((event) => event.type === 'room:cleared')) this.portal.startExitBurst();

    for (const e of events) {
      if (e.type !== 'ranged:fired') continue;
      const material = new THREE.MeshBasicMaterial({
        color: PALETTE.fireball,
        transparent: true,
        depthTest: false,
      });
      const mesh = new THREE.Mesh(this.beamGeometry, material);
      mesh.scale.set(e.tiles, 1, 1);
      // Facing right, the shot covers tiles [tx, tx+tiles) - centre tx + tiles/2.
      // Facing left, it covers tiles (tx-tiles, tx] - i.e. world-x span
      // [tx-tiles+1, tx+1) - centre tx + 1 - tiles/2. Verified by hand against
      // a concrete 3-tile shot in both directions before writing this.
      const half = e.tiles / 2;
      const centerX = e.facing === 1 ? e.tx + half : e.tx + 1 - half;
      mesh.position.set(centerX, -(e.ty + 0.5), 0.4);
      this.scene.add(mesh);
      this.activeBeams.push({ mesh, life: BEAM_LIFETIME });
    }
    for (let i = this.activeBeams.length - 1; i >= 0; i--) {
      const beam = this.activeBeams[i]!;
      beam.life -= deltaSeconds;
      if (beam.life <= 0) {
        this.scene.remove(beam.mesh);
        (beam.mesh.material as THREE.Material).dispose();
        this.activeBeams.splice(i, 1);
        continue;
      }
      (beam.mesh.material as THREE.MeshBasicMaterial).opacity = beam.life / BEAM_LIFETIME;
    }

    const tileCounts = this.tileCounts;
    for (const kind of this.tileMeshes.keys()) tileCounts.set(kind, 0);
    let hitCrackCount = 0;
    for (let ty = 0; ty < GRID_H; ty++) {
      for (let tx = 0; tx < GRID_W; tx++) {
        const i = idx(tx, ty);
        const kind = (s.tiles[i] ?? Tile.Empty) as TileKind;
        if (kind === Tile.Empty) continue;
        const mesh = this.tileMeshes.get(kind);
        if (!mesh) continue;
        const n = tileCounts.get(kind) ?? 0;
        const ph = s.phase[i] ?? Phase.Settled;
        // Spawning and crumbling blocks are not solid; show that by shrinking
        // them, so the player can read the window they can cast through.
        const t = ph === Phase.Settled ? 1 : 0.55;
        this.dummy.position.set(tx + 0.5, -(ty + 0.5), 0);
        this.dummy.scale.set(t, t, t);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(n, this.dummy.matrix);
        this.color.setHex(
          kind === Tile.Stone ? PALETTE.stone : kind === Tile.Vine ? PALETTE.vine : PALETTE.seed,
        );
        // Blocks that have taken a head-butt lighten toward white, one step per
        // hit, so the player can see which ones are one jump from breaking.
        const hits = s.hits[i] ?? 0;
        if (hits > 0) {
          const damage = Math.min(1, hits / BLOCK_HITS_TO_BREAK);
          this.color.lerp(HIT_TINT, HIT_TINT_MAX * damage);
        }
        mesh.setColorAt(n, this.color);
        tileCounts.set(kind, n + 1);

        // The second head-butt destroys a castable block during simulation, so
        // a visible `hits === 1` is precisely the one-hit state. Stone never
        // accumulates break damage and intentionally receives no decal.
        if (hits === 1 && (kind === Tile.Seed || kind === Tile.Vine)) {
          this.hitCrackDummy.position.set(tx + 0.5, -(ty + 0.5), 0.03);
          this.hitCrackDummy.scale.set(t, t, 1);
          this.hitCrackDummy.rotation.z = ((tx * 7 + ty * 11) % 4) * (Math.PI / 2);
          this.hitCrackDummy.updateMatrix();
          this.hitCrackMesh.setMatrixAt(hitCrackCount++, this.hitCrackDummy.matrix);
        }
      }
    }
    for (const [kind, mesh] of this.tileMeshes) {
      mesh.count = tileCounts.get(kind) ?? 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    this.hitCrackMesh.count = hitCrackCount;
    this.hitCrackMesh.instanceMatrix.needsUpdate = true;

    let m = 0;
    let moonseedItem: SimState['items'][number] | undefined;
    for (const it of s.items) {
      if (it.kind === ItemKind.Moonseed) moonseedItem = it;
      if (it.taken || it.hidden) continue;
      // Once the real emblem has loaded, it - not the generic gem - is the
      // Moonseed's visual (see `moonseedEmblem.sync` below). Until then this
      // instance keeps standing in for it, same fallback shape as the Axie
      // GLB's `PlaceholderAxie`.
      if (it.kind === ItemKind.Moonseed && this.moonseedEmblem) continue;
      this.dummy.position.set(it.tx + 0.5, -(it.ty + 0.5), 0.4);
      this.dummy.rotation.z = s.tick * 0.03;
      this.dummy.scale.setScalar(it.kind === 'moonseed' ? 1.35 : 0.8);
      this.dummy.updateMatrix();
      this.itemMesh.setMatrixAt(m++, this.dummy.matrix);
    }
    this.itemMesh.count = m;
    this.itemMesh.instanceMatrix.needsUpdate = true;
    this.dummy.rotation.z = 0;
    this.moonseedEmblem?.sync(moonseedItem, s.tick);

    for (const door of s.doors) {
      let mesh = this.doorMeshes.get(door.id);
      if (!mesh) {
        const texture = makeDoorTexture(door.label, door.locked);
        const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
        mesh = new THREE.Mesh(this.doorGeometry, material);
        mesh.name = `door-${door.label}`;
        this.scene.add(mesh);
        this.doorMeshes.set(door.id, mesh);
      }
      // Buried under a cast block (brief pillar 5, generalized from the
      // item-hidden mechanic) - kept alive rather than removed, since it
      // reappears the moment the covering block is destroyed.
      mesh.visible = !door.hidden;
      if (door.hidden) continue;
      const bob = Math.sin(s.tick * 0.04 + door.id) * 0.04;
      mesh.position.set(door.tx + 0.5, -(door.ty + 0.5) + bob, 0.25);
    }

    const aliveChimeraIds = new Set<number>();
    for (const ch of s.chimera) {
      if (!ch.alive) continue;
      aliveChimeraIds.add(ch.id);
      let visual = this.chimeraModels.get(ch.id);
      if (!visual) {
        const model = createSlimeBuddyModel();
        this.scene.add(model);
        // Deterministic per-instance offset (golden-angle-ish step, not
        // `Math.random()`) so a room full of Chimeras doesn't bounce in
        // lockstep, and a replay looks identical every run.
        visual = { model, phase: (ch.id * 2.399963) % (Math.PI * 2), prevX: ch.x, prevY: ch.y, hopT: 0, facing: 1 };
        this.chimeraModels.set(ch.id, visual);
      }
      const { model } = visual;
      const isCreep = ch.kind === ChimeraKind.Creep;
      const isWarden = ch.kind === ChimeraKind.Warden;

      // How far this Chimera actually moved since the last rendered frame.
      // A Creep slides continuously in small steps; a Drifter/Warden jumps a
      // whole tile in one simulation step, which - even batched across a
      // catch-up frame - is far larger than a Creep could ever cover in the
      // same span. That size difference, not the Chimera's kind, is what
      // decides which animation plays.
      const dx = ch.x - visual.prevX;
      const dy = ch.y - visual.prevY;
      const dist = Math.hypot(dx, dy);
      visual.prevX = ch.x;
      visual.prevY = ch.y;
      // Real observed horizontal travel, not the `dir` field - only a Creep's
      // `dir` has movement meaning (brief §5); a Drifter/Warden turns toward
      // whichever way it actually just stepped. Kept from the previous frame
      // when not moving, so it never flickers back to a default while idle.
      if (Math.abs(dx) > 1e-3) visual.facing = dx > 0 ? 1 : -1;

      if (dist > CHIMERA_HOP_DISTANCE) visual.hopT = CHIMERA_HOP_DURATION;
      else visual.hopT = Math.max(0, visual.hopT - deltaSeconds);
      // 0 -> 1 -> 0 pop-and-settle over the hop's duration.
      const hopEase = visual.hopT > 0 ? Math.sin((1 - visual.hopT / CHIMERA_HOP_DURATION) * Math.PI) : 0;

      // Continuous slide stretch is a Creep-only read: a Drifter/Warden's
      // one-frame position spike would otherwise register as an enormous
      // "speed" too, double-counting the hop pop above for no visual gain.
      const speed = dist / Math.max(deltaSeconds, 1e-4);
      const slideT = isCreep ? THREE.MathUtils.clamp(speed / CREEP_SPEED, 0, 1) : 0;

      // Continuous idle bounce/jiggle, always running, phase-offset per
      // instance - the baseline "alive" read every Chimera gets regardless
      // of whether it is currently moving.
      const idleAngle = s.tick * CHIMERA_IDLE_RATE + visual.phase;
      const idleBob = Math.sin(idleAngle) * CHIMERA_IDLE_BOB;
      const idleSquash = Math.sin(idleAngle) * CHIMERA_IDLE_SQUASH;

      model.position.set(
        isCreep ? ch.x : ch.x + 0.5,
        (isCreep ? -(ch.y - 0.4) : -(ch.y + 0.5)) + idleBob,
        0.2,
      );
      // Bigger silhouette, not just a colour swap: §11 requires Chimera to be
      // told apart by shape, colour being a second, non-load-bearing cue.
      const baseScale = CHIMERA_MODEL_SCALE * (isWarden ? 1.3 : 1);
      const stretchY = 1 - idleSquash * 0.5 - slideT * CHIMERA_SLIDE_SQUASH + hopEase * CHIMERA_HOP_SQUASH;
      const stretchX = 1 + idleSquash * 0.35 + slideT * CHIMERA_SLIDE_SQUASH * 0.7 - hopEase * CHIMERA_HOP_SQUASH * 0.5;
      model.scale.set(baseScale * stretchX, baseScale * stretchY, baseScale * stretchX);
      // Yaw toward the way it's moving rather than mirroring the scale, same
      // reasoning as `GlbAxie.setFacing`: a negative scale flips the mesh's
      // winding/normals and lights it inside-out. A quarter-turn keeps it
      // facing camera-ish from either side instead of showing its back.
      model.rotation.y = visual.facing === 1 ? CHIMERA_FACING_YAW : -CHIMERA_FACING_YAW;
    }
    // Drop the model for any Chimera that died or was otherwise removed this
    // frame - same stale-bounding-sphere-adjacent cleanup discipline as the
    // rest of this renderer: nothing here is ever left pointing at state that
    // no longer exists.
    for (const [id, visual] of this.chimeraModels) {
      if (aliveChimeraIds.has(id)) continue;
      this.scene.remove(visual.model);
      visual.model.traverse((node) => {
        const mesh = node as THREE.Mesh;
        if (mesh.isMesh) mesh.geometry.dispose();
      });
      this.chimeraModels.delete(id);
    }

    const p = s.player;
    // Simulation y grows downward and the player's y is its feet; the character's
    // origin is its feet too, so this is the only place the flip happens.
    this.character.object3D.position.set(p.x, -p.y, 0.5);
    this.character.setFacing(p.facing);
    // Grounded and actually covering ground, not just holding a direction key
    // against a wall or mid-jump drift - the walk clip should track real
    // motion, not input.
    this.character.setMoving(p.grounded && Math.abs(p.vx) > 0.05);
    this.character.update(deltaSeconds);

    // Buried under a cast block, same generalized hidden mechanic as items
    // and doors.
    this.portal.sync(s.portal, p.hasMoonseed, s.tick, deltaSeconds);

    this.slimeBuddy?.update(deltaSeconds);
    this.emerald?.update(deltaSeconds);

    this.renderer.render(this.scene, this.camera);
  }

  /** Releases every GPU resource this instance allocated. Must be called on
   *  unmount, including Strict Mode's throwaway first mount. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    for (const visual of this.chimeraModels.values()) {
      visual.model.traverse((node) => {
        const mesh = node as THREE.Mesh;
        if (mesh.isMesh) mesh.geometry.dispose();
      });
    }
    this.chimeraModels.clear();
    // The geometry is shared (already in `disposables`); each door's own
    // texture and material are not.
    for (const mesh of this.doorMeshes.values()) {
      const material = mesh.material as THREE.MeshBasicMaterial;
      material.map?.dispose();
      material.dispose();
    }
    this.doorMeshes.clear();
    // The geometry is shared (already in `disposables`); each beam's own
    // material is not - same reasoning as the doors just above.
    for (const beam of this.activeBeams) (beam.mesh.material as THREE.Material).dispose();
    this.activeBeams.length = 0;
    this.slimeBuddy?.dispose();
    this.slimeBuddy = null;
    this.emerald?.dispose();
    this.emerald = null;
    this.moonseedEmblem?.dispose();
    this.moonseedEmblem = null;
    this.character.dispose();
    this.scene.clear();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.canvas.remove();
  }
}
